import { useCallback, useRef, useState } from 'react';
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Check, Loader2, RotateCcw, RotateCw, Upload, X } from 'lucide-react';

interface ImageCropperProps {
  open: boolean;
  onClose: () => void;
  onCropComplete: (croppedImageBlob: Blob) => void;
  aspectRatio?: number;
  title?: string;
  isUploading?: boolean;
}

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

export function ImageCropper({
  open,
  onClose,
  onCropComplete,
  aspectRatio = 3 / 4,
  title = 'Recadrer votre photo',
  isUploading = false,
}: ImageCropperProps) {
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState<Crop>();
  const [rotation, setRotation] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
        setRotation(0);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspectRatio));
    },
    [aspectRatio],
  );

  const rotateLeft = () => {
    setRotation((prev) => prev - 90);
  };

  const rotateRight = () => {
    setRotation((prev) => prev + 90);
  };

  const getCroppedImg = useCallback(async (): Promise<Blob | null> => {
    if (!imgRef.current || !crop) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const _scaleX = image.naturalWidth / image.width;
    const _scaleY = image.naturalHeight / image.height;

    // Calculate crop dimensions in natural size
    const cropX = (crop.x / 100) * image.naturalWidth;
    const cropY = (crop.y / 100) * image.naturalHeight;
    const cropWidth = (crop.width / 100) * image.naturalWidth;
    const cropHeight = (crop.height / 100) * image.naturalHeight;

    // Set canvas size to 300px width while maintaining aspect ratio
    const targetWidth = 300;
    const targetHeight = (cropHeight / cropWidth) * targetWidth;
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // Handle rotation
    if (rotation !== 0) {
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) return null;

      const radians = (rotation * Math.PI) / 180;
      const sin = Math.abs(Math.sin(radians));
      const cos = Math.abs(Math.cos(radians));

      // Calculate rotated image dimensions
      const rotatedWidth = image.naturalWidth * cos + image.naturalHeight * sin;
      const rotatedHeight = image.naturalHeight * cos + image.naturalWidth * sin;

      tempCanvas.width = rotatedWidth;
      tempCanvas.height = rotatedHeight;

      tempCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
      tempCtx.rotate(radians);
      tempCtx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

      // Draw cropped and rotated portion to final canvas
      ctx.drawImage(
        tempCanvas,
        cropX * (rotatedWidth / image.naturalWidth),
        cropY * (rotatedHeight / image.naturalHeight),
        cropWidth * (rotatedWidth / image.naturalWidth),
        cropHeight * (rotatedHeight / image.naturalHeight),
        0,
        0,
        targetWidth,
        targetHeight,
      );
    } else {
      // Draw cropped portion directly
      ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, targetWidth, targetHeight);
    }

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/png',
        1,
      );
    });
  }, [crop, rotation]);

  const handleCrop = async () => {
    const croppedBlob = await getCroppedImg();
    if (croppedBlob) {
      onCropComplete(croppedBlob);
    }
  };

  const handleClose = () => {
    setImgSrc('');
    setCrop(undefined);
    setRotation(0);
    onClose();
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <input
            type="file"
            accept="image/*"
            onChange={onSelectFile}
            ref={fileInputRef}
            className="hidden"
          />

          {!imgSrc ? (
            <button
              type="button"
              onClick={triggerFileInput}
              className="w-full border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 hover:bg-primary-surface/30 transition-colors"
            >
              <div className="h-16 w-16 mx-auto bg-primary-surface rounded-full flex items-center justify-center mb-4">
                <Upload className="h-8 w-8 text-primary" />
              </div>
              <p className="text-lg font-medium mb-1">Cliquez pour sélectionner une photo</p>
              <p className="text-sm text-muted-foreground">PNG, JPG, GIF jusqu'à 5MB</p>
            </button>
          ) : (
            <>
              <div className="flex justify-center gap-2 mb-4">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={rotateLeft}
                  disabled={isUploading}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  -90°
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={rotateRight}
                  disabled={isUploading}
                >
                  <RotateCw className="h-4 w-4 mr-1" />
                  +90°
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={triggerFileInput}
                  disabled={isUploading}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Autre photo
                </Button>
              </div>

              <div className="flex justify-center bg-muted/30 rounded-lg p-4 overflow-hidden">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  aspect={aspectRatio}
                  className="max-h-[400px]"
                >
                  <img
                    ref={imgRef}
                    src={imgSrc}
                    alt="Crop preview"
                    onLoad={onImageLoad}
                    style={{
                      transform: `rotate(${rotation}deg)`,
                      maxHeight: '400px',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </ReactCrop>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isUploading}>
            <X className="h-4 w-4 mr-2" />
            Annuler
          </Button>
          {imgSrc && (
            <Button type="button" onClick={handleCrop} disabled={!crop || isUploading}>
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Valider
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
